import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { useLang } from "../../../i18n/LanguageContext";
import { dreamsClient } from "../api/dreamsClient";
import {
  blockDreamEditorDrop,
  handlePlainTextPaste,
  sanitizeDreamHtml,
  scrubDreamEditor,
} from "../utils/safeDreamHtml";

const DREAM_EMOTIONS = ["Spokój", "Radość", "Strach", "Smutek", "Złość", "Zaskoczenie"];
const SAVE_MODE = "e2e";

const getEditDreamCopy = (lang) => ({
  noE2E: lang === "en" ? "This dream has no encrypted E2E body." : "Ten sen nie ma zaszyfrowanej treści E2E.",
  vaultNotReadyShort: lang === "en" ? "The E2E vault is not ready yet." : "Sejf E2E nie jest jeszcze gotowy.",
  unlockFailedShort: lang === "en" ? "Could not unlock the vault." : "Nie udało się odblokować sejfu.",
  gateEyebrow: lang === "en" ? "Dream gate" : "Senna brama",
  gateTitlePre: lang === "en" ? "Welcome to" : "Witaj w",
  gateSubtitle: lang === "en" ? "Sign in to edit a dream in your Dream Codex." : "Zaloguj się, aby edytować sen w swojej Księdze Snów.",
  signIn: lang === "en" ? "🔑 Sign in" : "🔑 Zaloguj się",
  register: lang === "en" ? "✨ Create account" : "✨ Załóż konto",
  home: lang === "en" ? "🏠 Home" : "🏠 Strona główna",
  loadingTitle: lang === "en" ? "Opening dream…" : "Otwieram sen…",
  loadingSubtitle: lang === "en" ? "Fetching the entry from the Dream Codex." : "Pobieram wpis z Księgi Snów.",
  missingDream: lang === "en" ? "Dream not found." : "Sen nie został znaleziony.",
  loadError: lang === "en" ? "Could not fetch or decrypt the dream." : "Nie udało się pobrać albo odszyfrować snu.",
  cannotEditTitle: lang === "en" ? "Cannot edit dream" : "Nie można edytować snu",
  cannotEditFallback: lang === "en" ? "Return to the Room or add a new dream." : "Wróć do Pokoiku albo dodaj nowy sen.",
  addDream: lang === "en" ? "Add new dream" : "Dodaj nowy sen",
  backRoom: lang === "en" ? "Back to Room" : "Wróć do Pokoiku",
  pageTitle: lang === "en" ? "Edit dream" : "Edytuj sen",
  pageSubtitle: lang === "en" ? "Change the body, emotions and symbols" : "Zmień treść, emocje i symbole",
  titleLabel: lang === "en" ? "Dream title" : "Tytuł snu",
  titlePlaceholder: lang === "en" ? "Dream title" : "Tytuł snu",
  bodyLabel: lang === "en" ? "Dream body" : "Treść snu",
  bodyPlaceholder: lang === "en" ? "Edit dream body..." : "Edytuj treść snu...",
  sideKicker: lang === "en" ? "Dream Codex" : "Księga Snów",
  sideTitle: lang === "en" ? "Entry settings" : "Ustawienia wpisu",
  dateLabel: lang === "en" ? "Dream date" : "Data snu",
  timeLabel: lang === "en" ? "Time" : "Godzina",
  emotionsLabel: lang === "en" ? "Emotions" : "Emocje",
  symbolsLabel: lang === "en" ? "Symbols" : "Symbole",
  symbolsPlaceholder: lang === "en" ? "e.g. train, bridge, rain" : "np. pociąg, most, deszcz",
  saveHelp: lang === "en"
    ? "Dreams in the Room are saved in the E2E vault. Morfeusz does not know the vault password and cannot recover it."
    : "Sny w Pokoiku są zapisywane w sejfie E2E. Morfeusz nie zna hasła sejfu i nie może go odzyskać.",
  missingTitle: lang === "en" ? "Enter a dream title." : "Podaj tytuł snu.",
  missingBody: lang === "en" ? "Describe the dream before saving." : "Opisz sen przed zapisem.",
  vaultNotReady: lang === "en" ? "The E2E vault is not ready yet. Refresh the page and try again." : "Sejf E2E nie jest jeszcze gotowy. Odśwież stronę i spróbuj ponownie.",
  unlocking: lang === "en" ? "Unlocking the vault…" : "Odblokowuję sejf…",
  unlockChangesFailed: lang === "en" ? "Could not unlock the vault. Changes were not saved." : "Nie udało się odblokować sejfu. Zmiany nie zostały zapisane.",
  encrypting: lang === "en" ? "Encrypting changes…" : "Szyfruję zmiany…",
  saving: lang === "en" ? "Saving changes…" : "Zapisuję zmiany…",
  saved: lang === "en" ? "Changes saved in the vault." : "Zapisano zmiany w sejfie.",
  sessionExpired: lang === "en" ? "Session expired. Sign in again to save changes." : "Sesja wygasła. Zaloguj się ponownie, aby zapisać zmiany.",
  saveFailed: lang === "en" ? "Could not save changes. Try again." : "Nie udało się zapisać zmian. Spróbuj ponownie.",
  deleteConfirm: lang === "en" ? "Delete this dream from your Dream Codex? This cannot be undone." : "Usunąć ten sen z Księgi Snów? Tej operacji nie można cofnąć.",
  deleting: lang === "en" ? "Deleting dream…" : "Usuwam sen…",
  deleteFailed: lang === "en" ? "Could not delete the dream. Try again." : "Nie udało się usunąć snu. Spróbuj ponownie.",
  deleteSubmit: lang === "en" ? "Delete dream" : "Usuń sen",
  deleteSubmitting: lang === "en" ? "Deleting…" : "Usuwam…",
  backDream: lang === "en" ? "Back to dream" : "Powrót do snu",
  submitSaving: lang === "en" ? "Saving…" : "Zapisuję…",
  submit: lang === "en" ? "Save changes" : "Zapisz zmiany",
  print: lang === "en" ? "Print" : "Drukuj",
});

const getEmotionLabel = (emotion, lang) => {
  if (lang !== "en") return emotion;
  const map = {
    Spokój: "Calm",
    Radość: "Joy",
    Strach: "Fear",
    Smutek: "Sadness",
    Złość: "Anger",
    Zaskoczenie: "Surprise",
  };
  return map[emotion] || emotion;
};

const getDreamIdFromHash = () => {
  if (typeof window === "undefined") return "";
  const match = (window.location.hash || "").match(/^#\/sen\/([^?]+)/);
  return match ? decodeURIComponent(match[1]) : "";
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const parseSymbols = (value) =>
  String(value || "").split(",").map((item) => item.trim()).filter(Boolean);

const isVaultUnlocked = () => {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.MDVaultSession?.getState?.()?.unlocked || window.MDVaultSession?.isUnlocked?.());
  } catch (_) {
    return false;
  }
};

const getDreamE2E = (dream) => dream?.e2e || { iv: "", ct: "", aad: "" };

const decryptDreamBody = async (dream, copy) => {
  const pack = getDreamE2E(dream);
  if (!pack.iv || !pack.ct) {
    throw new Error(copy.noE2E);
  }
  if (!window.MDVaultSession || !window.morfeuszE2E) {
    throw new Error(copy.vaultNotReadyShort);
  }
  if (!isVaultUnlocked()) {
    const unlocked = await window.MDVaultSession.ensureUnlocked();
    if (!unlocked?.ok && !unlocked?.state?.unlocked) {
      throw new Error(copy.unlockFailedShort);
    }
  }
  const plain = await window.morfeuszE2E.decryptText(pack.iv, pack.ct, pack.aad || "");
  return sanitizeDreamHtml(plain);
};

const normalizeDreamForEdit = (dream, bodyHtml = "") => {
  if (!dream) return null;
  const meta = dream.meta || {};
  return {
    id: dream.id || dream.wpId,
    title: dream.title || dream.sen_tytul || meta.title || "",
    date: dream.date || meta.date || dream.sen_data || "",
    time: dream.time || meta.time || dream.sen_godzina || "",
    bodyHtml: sanitizeDreamHtml(bodyHtml),
    symbols: normalizeArray(dream.symbols || meta.symbols || dream.sen_symbole || "").join(", "),
    emotions: normalizeArray(dream.emotions || meta.emotions || dream.sen_emocje || []),
    saveMode: SAVE_MODE,
    e2e: getDreamE2E(dream),
  };
};

const useSettingsColumnHeight = (gridRef, sideRef, deps = []) => {
  useEffect(() => {
    const grid = gridRef.current;
    const side = sideRef.current;
    if (!grid || !side || typeof window === "undefined") return undefined;

    let frame = 0;
    const applyHeight = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (window.matchMedia("(max-width: 900px)").matches) {
          grid.style.removeProperty("--dream-settings-height");
          return;
        }
        const height = Math.ceil(side.getBoundingClientRect().height);
        if (height > 0) grid.style.setProperty("--dream-settings-height", `${height}px`);
      });
    };

    applyHeight();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(applyHeight) : null;
    observer?.observe(side);
    window.addEventListener("resize", applyHeight);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", applyHeight);
    };
  }, deps);
};

const PokoikGate = ({ gate, copy }) => (
  <section className="mdPage" data-ui="gate">
    <div className="mdPage__inner">
      <div>
        <p className="mdPage__eyebrow">{copy.gateEyebrow}</p>
        <h1 className="mdPage__title">{copy.gateTitlePre} <em>Morfeusz Dreams</em></h1>
        <p className="mdPage__subtitle">{copy.gateSubtitle}</p>
      </div>
      <div className="mdCard">
        <div className="mdRow">
          <a className="mdBtn mdBtn--primary mdBtn--row" data-md-login="1" href={gate.links.login}>{copy.signIn}</a>
          <a className="mdBtn mdBtn--row" href={gate.links.register}>{copy.register}</a>
          <a className="mdBtn mdBtn--row" href={gate.links.home}>{copy.home}</a>
        </div>
      </div>
    </div>
  </section>
);

const EditDreamLoading = ({ copy }) => (
  <section className="dream-container" id="dodaj-sen" data-ui="edit-dream-loading">
    <header className="dream-header">
      <div className="dream-icon" aria-hidden="true">✏️</div>
      <h1 className="dream-title">{copy.loadingTitle}</h1>
      <div className="dream-date">{copy.loadingSubtitle}</div>
    </header>
  </section>
);

export const EditDreamPage = () => {
  const { lang } = useLang();
  const { isAuthenticated, authReady } = useAuth();
  const copy = useMemo(() => getEditDreamCopy(lang), [lang]);
  const editorRef = useRef(null);
  const formGridRef = useRef(null);
  const sideRef = useRef(null);
  const dreamId = getDreamIdFromHash();

  const [dream, setDream] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [symbols, setSymbols] = useState("");
  const [emotions, setEmotions] = useState([]);
  const [status, setStatus] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toolbarState, setToolbarState] = useState({ bold: false, italic: false, underline: false, hrFlash: false });

  useSettingsColumnHeight(formGridRef, sideRef, [emotions.length, status, isSaving, isDeleting, dream?.id]);

  useEffect(() => {
    let mounted = true;

    const loadDream = async () => {
      if (!authReady) return;
      if (!isAuthenticated) {
        setIsLoading(false);
        setDream(null);
        setLoadError("");
        return;
      }
      if (!dreamId) {
        setIsLoading(false);
        setDream(null);
        setLoadError(copy.missingDream);
        return;
      }

      setIsLoading(true);
      setLoadError("");
      setStatus("");

      try {
        const response = await dreamsClient.get(dreamId);
        const rawDream = response?.dream || null;
        const bodyHtml = rawDream ? await decryptDreamBody(rawDream, copy) : "";
        if (!mounted) return;
        const nextDream = normalizeDreamForEdit(rawDream, bodyHtml);
        setDream(nextDream);
        if (!nextDream) setLoadError(copy.missingDream);
      } catch (error) {
        if (!mounted) return;
        setDream(null);
        setLoadError(error?.message || copy.loadError);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadDream();
    return () => { mounted = false; };
  }, [authReady, isAuthenticated, dreamId, copy]);

  useEffect(() => {
    if (!dream) return;
    setTitle(dream.title);
    setDate(dream.date);
    setTime(dream.time);
    setSymbols(dream.symbols);
    setEmotions(dream.emotions);
    if (editorRef.current) {
      editorRef.current.innerHTML = sanitizeDreamHtml(dream.bodyHtml || "");
      scrubDreamEditor(editorRef.current);
    }
  }, [dream?.id]);

  if (!authReady || isLoading) return <EditDreamLoading copy={copy} />;

  if (!isAuthenticated) {
    return <PokoikGate copy={copy} gate={{
      links: {
        login: `#/zaloguj?redirect_to=/sen/${encodeURIComponent(dreamId)}?edit=1`,
        register: `#/rejestracja?redirect_to=/sen/${encodeURIComponent(dreamId)}?edit=1`,
        home: "#top",
      },
    }} />;
  }

  if (!dream) {
    return (
      <section className="dream-container" id="dodaj-sen" data-ui="edit-dream-root">
        <header className="dream-header">
          <div className="dream-icon" aria-hidden="true">✏️</div>
          <h1 className="dream-title">{copy.cannotEditTitle}</h1>
          <div className="dream-date">{loadError || copy.cannotEditFallback}</div>
        </header>
        <div className="dream-bottom-actions" data-ui="dream-actions-row">
          <a className="md-btn md-primary" href="#/dodaj-sen">{copy.addDream}</a>
          <a className="md-btn md-secondary" href="#/pokoik">{copy.backRoom}</a>
        </div>
      </section>
    );
  }

  const refreshToolbarState = () => {
    try {
      setToolbarState((prev) => ({
        ...prev,
        bold: !!document.queryCommandState("bold"),
        italic: !!document.queryCommandState("italic"),
        underline: !!document.queryCommandState("underline"),
      }));
    } catch (_) {}
  };

  const runCommand = (command) => {
    editorRef.current?.focus();
    document.execCommand(command, false, null);
    scrubDreamEditor(editorRef.current);
    if (["bold", "italic", "underline"].includes(command)) {
      window.requestAnimationFrame(refreshToolbarState);
      return;
    }
    if (command === "insertHorizontalRule") {
      setToolbarState((prev) => ({ ...prev, hrFlash: true }));
      window.setTimeout(() => setToolbarState((prev) => ({ ...prev, hrFlash: false })), 220);
    }
  };

  const handleToolbarMouseDown = (event, command) => {
    event.preventDefault();
    runCommand(command);
  };

  const toggleEmotion = (emotion) => {
    setEmotions((prev) => prev.includes(emotion) ? prev.filter((item) => item !== emotion) : [...prev, emotion]);
  };

  const handleEditorInput = () => {
    refreshToolbarState();
  };

  const handlePaste = (event) => {
    const handled = handlePlainTextPaste(event, editorRef.current);
    if (handled) refreshToolbarState();
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (isSaving || isDeleting) return;

    const editor = editorRef.current;
    if (editor) scrubDreamEditor(editor);
    const bodyHtml = sanitizeDreamHtml(editor?.innerHTML || "");
    if (editor && editor.innerHTML !== bodyHtml) editor.innerHTML = bodyHtml;
    const bodyText = editor?.innerText?.trim() || "";

    if (!title.trim()) {
      setStatus(copy.missingTitle);
      return;
    }
    if (!bodyText) {
      setStatus(copy.missingBody);
      return;
    }
    if (!window.MDVaultSession || !window.morfeuszE2E) {
      setStatus(copy.vaultNotReady);
      return;
    }

    const nextSymbols = parseSymbols(symbols);
    setIsSaving(true);
    setStatus(copy.unlocking);

    try {
      const unlocked = await window.MDVaultSession.ensureUnlocked();
      if (!unlocked?.ok && !unlocked?.state?.unlocked) {
        setStatus(copy.unlockChangesFailed);
        return;
      }

      setStatus(copy.encrypting);
      const encrypted = await window.morfeuszE2E.encryptText(bodyHtml);
      const patch = {
        title: title.trim(),
        date,
        time,
        saveMode: SAVE_MODE,
        save_mode: SAVE_MODE,
        bodyHtml: "",
        sen_opis: "",
        e2e: encrypted,
        symbols: symbols.trim(),
        emotions,
        sen_tytul: title.trim(),
        sen_data: date,
        sen_godzina: time,
        sen_symbole: symbols.trim(),
        sen_emocje: emotions,
        meta: { title: title.trim(), date, time, symbols: nextSymbols, emotions },
      };

      setStatus(copy.saving);
      const response = await dreamsClient.update(dream.id, patch);
      setDream(normalizeDreamForEdit(response?.dream || { ...dream, ...patch }, bodyHtml));
      setStatus(copy.saved);
    } catch (error) {
      setStatus(error?.code === "unauthorized" ? copy.sessionExpired : (error?.message || copy.saveFailed));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isSaving || isDeleting) return;
    if (!window.confirm(copy.deleteConfirm)) return;

    setIsDeleting(true);
    setStatus(copy.deleting);

    try {
      await dreamsClient.remove(dream.id);
      window.location.hash = "#/moje-sny";
    } catch (error) {
      setStatus(error?.code === "unauthorized" ? copy.sessionExpired : (error?.message || copy.deleteFailed));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <section className="dream-container" id="dodaj-sen" data-ui="edit-dream-root" data-dream-id={dream.id}>
      <header className="dream-header">
        <div className="dream-icon" aria-hidden="true">✏️</div>
        <h1 className="dream-title">{copy.pageTitle}</h1>
        <div className="dream-date">{copy.pageSubtitle}</div>
      </header>

      <form className="dream-content-card" data-ui="edit-dream-form" onSubmit={handleSave}>
        <div className="dream-form-grid" ref={formGridRef}>
          <div data-ui="add-dream-main">
            <div className="field">
              <label htmlFor="edit-dream-title-input">{copy.titleLabel}</label>
              <input id="edit-dream-title-input" className="md-input md-title-input" type="text" value={title} onChange={(event) => setTitle(event.target.value)} placeholder={copy.titlePlaceholder} maxLength={160} />
            </div>

            <div className="field">
              <label>{copy.bodyLabel}</label>
              <div className="md-toolbar" data-ui="add-dream-toolbar">
                <button type="button" className={`md-btn ${toolbarState.bold ? "is-active" : ""}`} onMouseDown={(event) => handleToolbarMouseDown(event, "bold")}>B</button>
                <button type="button" className={`md-btn ${toolbarState.italic ? "is-active" : ""}`} onMouseDown={(event) => handleToolbarMouseDown(event, "italic")}>I</button>
                <button type="button" className={`md-btn ${toolbarState.underline ? "is-active" : ""}`} onMouseDown={(event) => handleToolbarMouseDown(event, "underline")}>U</button>
                <button type="button" className={`md-btn ${toolbarState.hrFlash ? "is-active" : ""}`} onMouseDown={(event) => handleToolbarMouseDown(event, "insertHorizontalRule")}>HR</button>
              </div>
              <div
                ref={editorRef}
                className="dream-editor"
                contentEditable
                data-placeholder={copy.bodyPlaceholder}
                onInput={handleEditorInput}
                onPaste={handlePaste}
                onDrop={(event) => blockDreamEditorDrop(event, editorRef.current)}
                onDragOver={(event) => blockDreamEditorDrop(event, editorRef.current)}
                onKeyUp={refreshToolbarState}
                onMouseUp={refreshToolbarState}
                onFocus={refreshToolbarState}
                suppressContentEditableWarning
              />
            </div>
          </div>

          <aside className="md-card" data-ui="add-dream-side" ref={sideRef}>
            <div className="add-dream-side-head" data-ui="add-dream-side-head">
              <div className="add-dream-side-kicker">{copy.sideKicker}</div>
              <h2>{copy.sideTitle}</h2>
            </div>

            <div className="field"><label htmlFor="edit-dream-date-input">{copy.dateLabel}</label><input id="edit-dream-date-input" className="md-input" type="date" value={date} onChange={(event) => setDate(event.target.value)} /></div>
            <div className="field"><label htmlFor="edit-dream-time-input">{copy.timeLabel}</label><input id="edit-dream-time-input" className="md-input" type="time" value={time} onChange={(event) => setTime(event.target.value)} /></div>

            <div className="field">
              <label>{copy.emotionsLabel}</label>
              <div className="md-chips" data-ui="add-dream-emotions" data-name="sen_emocje[]">
                {DREAM_EMOTIONS.map((emotion) => (
                  <button key={emotion} type="button" className={`md-chip-btn ${emotions.includes(emotion) ? "active" : ""}`} data-val={emotion} onClick={() => toggleEmotion(emotion)}>{getEmotionLabel(emotion, lang)}</button>
                ))}
              </div>
            </div>

            <div className="field">
              <label htmlFor="edit-dream-symbols-input">{copy.symbolsLabel}</label>
              <input id="edit-dream-symbols-input" className="md-input" type="text" value={symbols} onChange={(event) => setSymbols(event.target.value)} placeholder={copy.symbolsPlaceholder} />
            </div>

            <div className="md-hint" data-ui="add-dream-save-help">{copy.saveHelp}</div>
          </aside>
        </div>

        {status && (
          <div className="md-alert md-info" data-ui="add-dream-status" role="status">
            <div data-ui="add-dream-status-text">{status}</div>
            <div className="md-cta-row" data-ui="add-dream-status-actions"><a className="md-btn md-secondary" href={`#/sen/${encodeURIComponent(dream.id)}`}>{copy.backDream}</a></div>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="md-btn md-primary" disabled={isSaving || isDeleting}>{isSaving ? copy.submitSaving : copy.submit}</button>
          <button type="button" className="md-btn md-secondary" data-ui="edit-dream-delete" onClick={handleDelete} disabled={isSaving || isDeleting}>{isDeleting ? copy.deleteSubmitting : copy.deleteSubmit}</button>
          <a className="md-btn md-secondary" href={`#/sen/${encodeURIComponent(dream.id)}`}>{copy.backDream}</a>
          <a className="md-btn md-secondary" href="#/pokoik">{copy.backRoom}</a>
          <button type="button" className="md-btn md-secondary" onClick={() => window.print()}>{copy.print}</button>
        </div>
      </form>
    </section>
  );
};
